/**
 * 节点参数弹层：portal 到 document.body 的 fixed 定位面板。
 *
 * 为什么不用节点内 absolute 弹层：节点内容层是 overflow-hidden 的圆角裁剪层
 * （NodeWrapper），弹层向下展开超出卡片底边的部分会被裁掉（比例/质量下拉
 * 显示不全的根因）；而 fixed 定位在有 transform 祖先（画布缩放容器）时会以
 * transform 为包含块失真。portal 到 body 后重算锚点屏幕坐标，是唯一同时
 * 不受裁剪层和画布缩放影响的路径。
 *
 * 关闭时机：点击弹层与锚点之外 / Escape / 滚轮（画布缩放平移）/ 窗口 resize。
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

interface NodeParamsPopoverProps {
  open: boolean;
  onClose: () => void;
  /** 锚点元素（参数按钮的外层容器），弹层右对齐其右缘 */
  anchor: RefObject<HTMLElement | null>;
  /** 弹层宽度（px），与旧 w-48/w-56 对齐 */
  width: number;
  children: ReactNode;
}

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

export function NodeParamsPopover({ open, onClose, anchor, width, children }: NodeParamsPopoverProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(VIEWPORT_MARGIN, rect.right - width);
    setPos({ top: rect.bottom + ANCHOR_GAP, left });
  }, [open, anchor, width]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // 锚点区域放行：由按钮自身的 onClick 负责开合切换
      if (anchor.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // 画布缩放（wheel）后锚点屏幕坐标变化，直接收起避免弹层悬空错位
    const onViewportChange = () => onClose();
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('wheel', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('wheel', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [open, onClose, anchor]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9000] space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
      style={{
        top: pos.top,
        left: pos.left,
        width,
        maxHeight: `calc(100vh - ${pos.top + VIEWPORT_MARGIN}px)`,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
